import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Box, Card, CardContent, TextField, Button, Typography, CircularProgress, Dialog, DialogContent } from '@material-ui/core';
import AppContext from '../AppContext';
import EmailLoginForm from '../components/EmailLoginForm';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import { ReactComponent as GoogleIcon } from '../resources/google-icon.svg';

const InvitePage = () => {
  const { isLoggedIn } = useContext(AppContext);
  const params = useParams();                // supports /invite/:code
  const location = useLocation();            // supports /invite?code=...
  const query = new URLSearchParams(location.search);

  const initialCode = params.code || query.get('code') || '';
  const [code, setCode] = useState(initialCode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cohort, setCohort] = useState(null); // { id, name, invite_code }
  const [link, setLink] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const [loginView, setLoginView] = useState('options'); // options | email

  const currentUrl = useMemo(() => {
    const path = location.pathname + location.search;
    return path || '/invite';
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (initialCode) {
      handleValidate(initialCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCode]);

  const handleValidate = async (raw) => {
    if (isLoggedIn === false) {
      setShowLogin(true);
      setLoginView('options');
      return;
    }
    const invite_code = (raw ?? code).trim();
    if (!invite_code) return;

    setLoading(true);
    setError('');
    setCohort(null);
    setLink('');

    try {
      // IMPORTANT: this endpoint should return 200 with {valid:false} when invalid
      const res = await fetch('/api/cohort/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ invite_code }),
      });

      // If your global fetch interceptor still redirects on 400,
      // change the backend to always return 200 with { valid: false }.
      const data = await res.json();

      if (data.valid) {
        const c = {
          id: data.cohortId || data.id || (data.cohort && data.cohort.id),
          name: data.name || (data.cohort && data.cohort.name),
          invite_code: invite_code,
        };
        setCohort(c);

        // Build the survey link. If backend provides survey_link, you can use that instead.
        const surveyUrl = `${window.location.origin}/questions?cohortId=${c.id}`;
        setLink(surveyUrl);
      } else {
        setError(data.message || 'Invalid invite code');
      }
    } catch (e) {
      setError('Unable to validate code right now.');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setError('Link copied to clipboard ✅');
      setTimeout(() => setError(''), 1800);
    } catch {
      setError('Could not copy to clipboard.');
    }
  };

  const ButtonRow = ({ children, style }) => (
    <Box mb={1.5}>
      <div style={{ width: '100%' }}>{children}</div>
    </Box>
  );

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: '#f6f7f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <Card style={{ width: 560, maxWidth: '95%' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Cohort Invite
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Paste an invite code to generate a survey link for that cohort.
          </Typography>

          <Box mt={2} display="flex" gridGap={12}>
            <TextField
              fullWidth
              label="Invite Code (e.g., C-24-ABC123)"
              variant="outlined"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleValidate()}
              disabled={loading}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleValidate()}
              disabled={loading || !code.trim()}
            >
              {loading ? <CircularProgress size={22} /> : 'Generate link'}
            </Button>
          </Box>

          {!!error && (
            <Typography style={{ marginTop: 12 }} color="error">
              {error}
            </Typography>
          )}

          {cohort && (
            <Box mt={3}>
              <Typography variant="subtitle1" gutterBottom>
                Cohort Details
              </Typography>
              <Typography variant="body2">
                <strong>ID:</strong> {cohort.id}
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {cohort.name || '—'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Invite Code:</strong> {cohort.invite_code}
              </Typography>

              <Box mt={2} p={2} style={{ background: '#f0f4ff', borderRadius: 8 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Generated Survey Link
                </Typography>
                {link ? (
                  <>
                    <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
                      <a href={link}>{link}</a>
                    </Typography>
                    <Box mt={1} display="flex" gridGap={8}>
                      <Button size="small" variant="outlined" onClick={copyLink}>
                        Copy
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        component={Link}
                        to={link.replace(window.location.origin, '')}
                      >
                        Open here
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={showLogin} onClose={() => setShowLogin(false)} maxWidth="xs" fullWidth>
        <DialogContent style={{ padding: 24 }}>
          {loginView === 'options' && (
            <Box>
              <Typography variant="h6" align="center" gutterBottom style={{ fontWeight: 600 }}>
                Warning:
              </Typography>
              <Typography variant="body2" align="center" style={{ marginBottom: 16, color: '#555' }}>
                Please note that logging in with an account with a different email will create a new account
              </Typography>

              <ButtonRow>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<GoogleIcon style={{ width: 20, height: 20 }} />}
                  style={{
                    justifyContent: 'flex-start',
                    backgroundColor: '#fff',
                    color: '#222',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                    border: '1px solid #e6e6e6',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                  onClick={() => {
                    const base = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001';
                    window.open(`${base}/api/auth/google?redirectUrl=${encodeURIComponent(currentUrl)}`, '_self');
                  }}
                >
                  Sign in with Google
                </Button>
              </ButtonRow>

              <ButtonRow>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<LinkedInIcon />}
                  style={{
                    justifyContent: 'flex-start',
                    backgroundColor: '#2867B2',
                    color: '#fff',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                  onClick={() => {
                    const base = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001';
                    window.open(`${base}/api/auth/linkedin?redirectUrl=${encodeURIComponent(currentUrl)}`, '_self');
                  }}
                >
                  Sign in with LinkedIn
                </Button>
              </ButtonRow>

              <ButtonRow>
                <Button variant="outlined" fullWidth onClick={() => setLoginView('email')} style={{ textTransform: 'none', fontWeight: 600 }}>
                  Login with Email
                </Button>
              </ButtonRow>
              <ButtonRow>
                <Button
                  variant="outlined"
                  fullWidth
                  style={{ textTransform: 'none', fontWeight: 600 }}
                  onClick={() => {
                    window.location.href = `/login?redirectUrl=${encodeURIComponent(currentUrl)}`;
                  }}
                >
                  Sign up with Email
                </Button>
              </ButtonRow>
            </Box>
          )}

          {loginView === 'email' && (
            <EmailLoginForm
              redirectUrl={currentUrl}
              onBack={() => setLoginView('options')}
              onSignup={() => (window.location.href = `/login?redirectUrl=${encodeURIComponent(currentUrl)}`)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default InvitePage;
